import type { FeatureSchemaResponse } from '@api/swagger/definitions/backoffice'
import { FeatureSchemaBackofficeApiApiFactory, FeaturesApiFactory, TemplatesApiFactory } from '@api/swagger/definitions/backoffice'
import type { Eligibility, PricePlanTemplate, TemplateFeature } from '@domains/subscription/priv/types'
import Axios from 'api/axios'
import config from 'config'
import {
  mapEligibilityToEligibilityDto,
  mapFeatureSchemaAndFeatureItemToTemplateFeature,
  mapFeatureSchemaDtoToFeature,
  mapTemplateDtoToTemplate,
  mapTemplateFeatureToFeatureDto,
} from './mappers'

const templatesApi = TemplatesApiFactory(undefined, config.boBaseUrl, Axios)
const featureApi = FeaturesApiFactory(undefined, config.boBaseUrl, Axios)
const featureSchemaApi = FeatureSchemaBackofficeApiApiFactory(undefined, config.boBaseUrl, Axios)

export const getTemplates = async (): Promise<PricePlanTemplate[]> => {
  return await templatesApi.getAllTemplates().then(({ data }) => data.map(mapTemplateDtoToTemplate))
}

export const getTemplate = async (id: string) => {
  return await templatesApi.getTemplate(id).then(({ data }) => mapTemplateDtoToTemplate(data))
}

export const createTemplate = async (name: string, externalName: string, eligibility: Eligibility) => {
  const swaggerEligibility = mapEligibilityToEligibilityDto(eligibility)
  return await templatesApi
    .createTemplate({ name, externalName, eligibility: swaggerEligibility })
    .then(({ data }) => mapTemplateDtoToTemplate(data))
}

export const deleteTemplate = async (_templateId: string): Promise<void> => {
  throw new Error('Not implemented yet')
}

export const getFeatures = async () => {
  return await featureSchemaApi
    .listFeatures()
    .then(({ data }) => data.filter((featureSchemaDto) => !!featureSchemaDto?.schema?.type).map(mapFeatureSchemaDtoToFeature))
}

export const getFeaturesForTemplate = async (templateId: string) => {
  const featureItems = await featureApi.allFeaturesForTemplate(templateId).then(({ data }) => data)

  const featureSchemaPromises = featureItems.map(async (feature) => await featureSchemaApi.getFeature(feature.id).then(({ data }) => data))
  const featureSchemas = await Promise.all(featureSchemaPromises)

  const templateFeatures = featureSchemas.map((featureSchema: FeatureSchemaResponse) => {
    const featureItem = featureItems.find((item) => item.id === featureSchema.featureId)

    if (!featureItem) {
      return null
    }

    return mapFeatureSchemaAndFeatureItemToTemplateFeature({ featureSchema, featureItem })
  })

  return templateFeatures.filter((feature): feature is TemplateFeature => feature !== null)
}

export const updateTemplate = async (templateId: string, features: TemplateFeature[]) => {
  const featureDtos = {
    features: features.map(mapTemplateFeatureToFeatureDto),
  }

  return await featureApi.replaceFeaturesForTemplate(templateId, featureDtos)
}
